import { store } from '../store';
import { showPermissionDenied } from '../actions/app';
import { isAuthorized } from '../types/permissions'; // Assuming this is where the function is located
import CryptoJS from 'crypto-js';
import { message } from 'antd';

const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY || 'oLirAwamiablxZXfvS4lfDeTW5yd4nvNsD6L0P1ZM5I=';

function encryptData(data) {
  // Convert the key from base64 to raw bytes
  const key = CryptoJS.enc.Base64.parse(ENCRYPTION_KEY);
  
  // Generate a random IV
  const iv = CryptoJS.lib.WordArray.random(16);
  
  // Encrypt the data
  const encrypted = CryptoJS.AES.encrypt(JSON.stringify(data), key, {
    iv: iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7
  });

  // Combine IV and ciphertext into a single string
  const ciphertext = encrypted.ciphertext;
  const ivAndCiphertext = CryptoJS.lib.WordArray.create()
    .concat(iv)
    .concat(ciphertext);
  
  // Convert to base64
  return CryptoJS.enc.Base64.stringify(ivAndCiphertext);
}

class ApiClient {
  constructor(baseURL) {
    this.baseURL = baseURL || '';
  }

  getAuthHeaders(projectId = null, resourceId = null) {
    const state = store.getState();
    const { user, token, isAuthenticated } = state.auth;

    if (!isAuthenticated || !user) {
      store.dispatch(showPermissionDenied());
      return null;
    }

    const payload = {
      userId: user.userId,
      nonce: Math.random().toString(36).substring(7),
      ...(projectId && { projectId }),
      ...(resourceId && { resourceId })
    };

    return {
      'Authorization': `Bearer ${token}`,
      'X-Encrypted-Data': encryptData(payload),
      'Content-Type': 'application/json'
    };
  }

  checkPermission(requiredPermission, projectId = null, resourceId = null, resourceType = null) {
    const state = store.getState();
    const { user, isAuthenticated } = state.auth;

    if (!isAuthenticated || !user) {
      store.dispatch(showPermissionDenied());
      return false;
    }

    // Use the isAuthorized function to check permissions
    const hasPermission = isAuthorized(user, projectId, resourceId, resourceType, requiredPermission);

    if (!hasPermission) {
      store.dispatch(showPermissionDenied());
    }

    return hasPermission;
  }

  async request(method, endpoint, { data = null, projectId = null, resourceId = null, resourceType = null,requiredPermission = null } = {}) {
    try {
      // Check authentication and permissions
      if (requiredPermission && !this.checkPermission(requiredPermission, projectId, resourceId, resourceType)) {
        store.dispatch(showPermissionDenied());
        return null;
      }

      // Get headers with encrypted authentication data
      const headers = this.getAuthHeaders(projectId, resourceId);
      if (!headers) {
        return null;
      }

      // Construct the full URL
      const url = `${this.baseURL}${endpoint}`;
      
      // Make the request
      const response = await fetch(url, {
        method,
        headers,
        body: data ? JSON.stringify(data) : undefined,
      });

      // Handle non-2xx responses
      if (!response.ok) {
        const error = await response.json();
        message.error(error.message || 'Request failed');
        return null;
      }

      // Parse and return the response
      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      message.error('An error occurred while making the request');
      return null;
    }
  }

  // Convenience methods for different HTTP methods
  async get(endpoint, options = {}) {
    return this.request('GET', endpoint, options);
  }

  async post(endpoint, data, options = {}) {
    return this.request('POST', endpoint, { ...options, data });
  }

  async put(endpoint, data, options = {}) {
    return this.request('PUT', endpoint, { ...options, data });
  }

  async delete(endpoint, options = {}) {
    return this.request('DELETE', endpoint, options);
  }
}

// Create and export a singleton instance
export const apiClient = new ApiClient('https://cloudify-prod.wizr.in');